import moment from "moment";
import {
  CYCLE_STATE,
  filterDateOperator,
  filterLogicalOperator,
  filterNumericOperator,
  filterTextOperator,
  SHIFT_TYPE,
} from './enums.js';

export function applyCustomFilters(data, filters, dataColumns) {
  const filterKeys = Object.keys(filters);
  if (filterKeys.length === 0) return data;

  return data.filter((row) => {
    return filterKeys.every((accessor) => {
      const filterObj = filters[accessor];
      if (!filterObj) return true;

      const col = dataColumns.find((c) => c.accessor === accessor);
      const columnType = col?.type || "string";

      const cellValue = row[accessor];
      return matchesFilter(cellValue, filterObj, columnType);
    });
  });
}

export function matchesFilter(cellValue, filterObj, columnType) {
  const { operator, value, value2 } = filterObj;

  switch (columnType) {
    case "number":
      return matchNumber(cellValue, operator, value, value2);
    case "date":
      return matchDate(cellValue, operator, value, value2);
    case "lookup":
      return matchLookup(cellValue, operator, value);
    default: // string
      return matchString(cellValue, operator, value);
  }
}

function matchString(cellValue, operator, val) {
  const text = (cellValue || "").toString().toLowerCase().replaceAll('-', '').trim();
  const check = (val || "").toString().toLowerCase().replaceAll('-', '').trim();

  switch (operator) {
    case "is":
      return text === check;
    case "not":
      return text !== check;
    case "contains":
      return text.includes(check);
    case "any_value":
      return text.length > 0;
    case "empty":
      return text === "";
    default:
      return true;
  }
}

function matchNumber(cellValue, operator, val, val2) {
  const num = Number(cellValue) || 0;
  const nVal = Number(val) || 0;
  const nVal2 = Number(val2) || 0;

  switch (operator) {
    case "equal":
      return num === nVal;
    case "between":
      return num >= nVal && num <= nVal2;
    case "greater":
      return num > nVal;
    case "less":
      return num < nVal;
    default:
      return true;
  }
}

function matchDate(cellValue, operator, val, val2) {
  const dateCell = new Date(cellValue);
  const dateVal = new Date(val);
  const dateVal2 = new Date(val2);

  switch (operator) {
    case "equal":
      return cellValue && moment(dateCell).isSame(dateVal, 'minutes');
    case "between":
      return cellValue && moment(dateCell).isBetween(dateVal, dateVal2);
    case "before":
      return cellValue && moment(dateCell).isBefore(dateVal2);
    case "after":
      return cellValue && moment(dateCell).isAfter(dateVal);
    default:
      return true;
  }
}

function matchLookup(cellValue, operator, valArray) {
  if (!Array.isArray(valArray)) return true;

  switch (operator) {
    case "is":
      if(Array.isArray(cellValue)) {
        return cellValue.some(value => valArray.map(v => v?.value).includes(value));
      }
      return valArray.includes(cellValue) || valArray.map(v => v?.value)?.includes(cellValue);
    default:
      return true;
  }
}

export function getOperatorFiltersLabel(operator, t) {
  switch (operator) {
      case "is":
      case "equal": return "";
      case "not": return t("orders.operators.IS_NOT");
      case "contains": return t("orders.operators.CONTAINS");
      case "any_value": return t("orders.operators.HAS_ANY_VALUE");
      case "empty": return t("orders.operators.EMPTY"); 
      case "between": return t("orders.operators.IS_BETWEEN");
      case "greater": return t("orders.operators.IS_GREATER_THAN");
      case "less": return t("orders.operators.IS_LASS_THAN");
      case "before": return t("orders.operators.EARLIER_THEN");
      case "after": return t("orders.operators.LATER_THEN");
      case "in": return t("orders.operators.IN");
      case "not_in": return t("orders.operators.NOT_IN"); 
      default: return operator;
  }
};

const formatDateFilter = (filter) => {
  const filterOption = {
    startDate: filter.value,
    endDate: filter.value2,
    operator: null,
  };

  const operatorMap = {
    equal: filterDateOperator.isEqualTo,
    between: filterDateOperator.isBetween,
    after: filterDateOperator.laterThan,
    before: filterDateOperator.earlierThan,
  };
  filterOption.operator = operatorMap[filter.operator];
  return { date: filterOption };
}

const formatTextFilter = filter => {
  const filterOption = {
    value: filter.value,
    operator: null,
  };

  const operatorMap = {
    is: filterTextOperator.is,
    not: filterTextOperator.isNot,
    contains: filterTextOperator.contains,
    any_value: filterTextOperator.hasAnyValue,
    empty: filterTextOperator.empty,
  };
  filterOption.operator = operatorMap[filter.operator];
  return { text: filterOption };
}

const formatNumericFilter = filter => {
  const filterOption = {
    minValue: filter.value,
    maxValue: filter.value2,
    operator: null,
  };

  const operatorMap = {
    equal: filterNumericOperator.equals,
    between: filterNumericOperator.isBetween,
    greater: filterNumericOperator.greaterThan,
    less: filterNumericOperator.lessThan,
  };
  filterOption.operator = operatorMap[filter.operator];
  return { numeric: filterOption };
}

const formatLookupFilter = filter => {
  if(typeof filter.value[0].key === "string") {
    return { stringLookup: { stringValues: filter.value.map(f => f.key) } };
  }
  return { intLookup: { intValues: filter.value.map(f => f.key) } };
}

export const filterFactoryMap = {
  date: formatDateFilter,
  string: formatTextFilter,
  number: formatNumericFilter,
  lookup: formatLookupFilter,
}

export const buildFilterModel = (filters, filterFieldTypes) => {
  return Object.entries(filters).map(([key, filter]) => {
    if(!filterFactoryMap[filter.type]) return null;
    return {
      field: filterFieldTypes[key],
      logic: filterLogicalOperator.and,
      ...filterFactoryMap[filter.type](filter),
    };
  }).filter(Boolean);
}

export const buildSortModel = (filters, filterFieldTypes) => {
  return Object.entries(filters).map(([key, filter]) => {
    if(!filter.sort) return null;
    return {
      field: filterFieldTypes[key],
      direction: filter.sort || 0,
    };
  }).filter(Boolean);
}

const checkSort = (courier, status) => {
  if(!courier || !courier.cycle) {
    return false;
  }
  switch(status) {
    case 'backup':
      return courier.cycle && courier.cycle.state === CYCLE_STATE.started && courier.cycle.shiftType === SHIFT_TYPE.Backup;
    case 'waiting':
      return courier.cycle && courier.cycle.state === CYCLE_STATE.started && courier.cycle.shiftType !== SHIFT_TYPE.Backup && !courier?.cycle?.orders?.length;
    case 'awaiting':
      return courier.cycle && courier.cycle.state === CYCLE_STATE.started && courier.cycle.shiftType !== SHIFT_TYPE.Backup && !!courier?.cycle?.orders?.length;
    case 'onroute':
      return courier.cycle && courier.cycle.state === CYCLE_STATE.deliveryStart;
    case 'ready':
      return courier.cycle && courier.cycle.state === CYCLE_STATE.approved;
    default:
      return false;
  }
}

export const filterCourierByStatus = (couriers, statusSort) => {
  return couriers.filter(courier => statusSort.some(status => checkSort(courier, status)))
}